/***************** Shipping Properties And Cart Attributes **********************/
/***************************************************************************************/

(() => {
  if (window.__liveocareShippingPropertiesInitialized) {
    return;
  }

  window.__liveocareShippingPropertiesInitialized = true;
  window.liveocareShippingTemplateCache = window.liveocareShippingTemplateCache || {};
  window.liveocareLastProductCardHandle = window.liveocareLastProductCardHandle || '';
  window.liveocareLastBuyAgainHandles = window.liveocareLastBuyAgainHandles || [];
  window.__liveocareCartShippingSyncRunning = false;
  window.__liveocareCartShippingSyncTimer = null;

  /***************** Shipping Template Helpers **********************/

  const getProductHandleFromUrl = (url) => {
    if (!url) {
      return '';
    }

    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/);

      return match && match[1] ? match[1] : '';
    } catch (error) {
      console.warn('LiveoCare Shipping: Product-Handle konnte nicht gelesen werden:', error);
      return '';
    }
  };

  const slugifyProductTitle = (value) => {
    return String(value || '')
      .split(' - ')[0]
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const getCurrentProductHandle = () => {
    const canonical = document.querySelector('link[rel="canonical"][href*="/products/"]');

    if (canonical) {
      const handle = getProductHandleFromUrl(canonical.href);

      if (handle) {
        return handle;
      }
    }

    return getProductHandleFromUrl(window.location.href);
  };

  const getProductLinkFromRoot = (root = document) => {
    return (
      root.querySelector?.('a.product-action-modal__view[href*="/products/"]') ||
      root.querySelector?.('a[href*="/products/"]') ||
      null
    );
  };

  const getProductHandleFromProductCardTrigger = (button) => {
    if (!button) {
      return '';
    }

    if (button.dataset.handle) {
      return button.dataset.handle;
    }

    if (button.dataset.productHandle) {
      return button.dataset.productHandle;
    }

    if (button.getAttribute('aria-label')) {
      return slugifyProductTitle(button.getAttribute('aria-label'));
    }

    const title = button.querySelector('.i-tl')?.textContent || '';

    return slugifyProductTitle(title);
  };

  const getProductHandleFromModalContext = (root = document) => {
    const productLink = getProductLinkFromRoot(root);

    if (productLink) {
      const handle = getProductHandleFromUrl(productLink.href);

      if (handle) {
        return handle;
      }
    }

    const activeTrigger = document.querySelector(
      '.product-card-trigger[aria-label].active, .product-card-trigger[aria-label][aria-expanded="true"]'
    );

    if (activeTrigger) {
      const handle = getProductHandleFromProductCardTrigger(activeTrigger);

      if (handle) {
        return handle;
      }
    }

    return window.liveocareLastProductCardHandle || '';
  };

  const getProductHandleFromContext = (root = document) => {
    const productLink = getProductLinkFromRoot(root);

    if (productLink) {
      const handle = getProductHandleFromUrl(productLink.href);

      if (handle) {
        return handle;
      }
    }

    const modalHandle = getProductHandleFromModalContext(root);

    if (modalHandle) {
      return modalHandle;
    }

    return getCurrentProductHandle();
  };

  const getProductHandleFromCustomerHubButton = (button) => {
    const root = button.closest(
      '.product-action-modal__dialog, .chProduct, .chItem, li, [class*="Order"], [class*="order"], [class*="Item"], [class*="item"]'
    ) || button.parentElement;

    const productLink = root?.querySelector?.('a[href*="/products/"]');

    if (productLink) {
      const handle = getProductHandleFromUrl(productLink.href);

      if (handle) {
        return handle;
      }
    }

    if (button.dataset.handle) {
      return button.dataset.handle;
    }

    if (button.dataset.productHandle) {
      return button.dataset.productHandle;
    }

    return slugifyProductTitle(button.dataset.itemNames || '');
  };

  const getProductHandlesFromLinks = (root) => {
    if (!root) {
      return [];
    }

    return Array.from(root.querySelectorAll('a[href*="/products/"]'))
      .map((link) => getProductHandleFromUrl(link.href))
      .filter(Boolean)
      .filter((handle, index, handles) => handles.indexOf(handle) === index);
  };

  const getProductHandlesFromOrderRoot = (root) => {
    if (!root) {
      return [];
    }

    const linkHandles = getProductHandlesFromLinks(root);

    if (linkHandles.length) {
      return linkHandles;
    }

    return Array.from(root.querySelectorAll('.order-items .i-tl, .o-i .i-tl, .ba-info h4'))
      .map((title) => slugifyProductTitle(title.textContent || ''))
      .filter(Boolean)
      .filter((handle, index, handles) => handles.indexOf(handle) === index);
  };

  const normalizeShippingTemplateData = (data, fallbackHandle = '') => {
    const productData = data.product || data;
    const variants = Array.isArray(productData.variants) ? productData.variants : [];

    return {
      handle: productData.handle || fallbackHandle,
      productId: productData.id || '',
      title: productData.title || '',
      minDays: parseInt(productData.shippingMinDays || '3', 10),
      maxDays: parseInt(productData.shippingMaxDays || '5', 10),
      variants,
      variantIds: variants.map((variant) => String(variant.id))
    };
  };

  const loadShippingTemplateDataByHandle = async (handle) => {
    if (!handle) {
      return null;
    }

    if (window.liveocareShippingTemplateCache[handle]) {
      return window.liveocareShippingTemplateCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}?view=shipping-data`);

      if (!response.ok) {
        console.warn('LiveoCare Shipping: shipping-data konnte nicht geladen werden:', handle, response.status);
        return null;
      }

      const raw = await response.text();
      const data = normalizeShippingTemplateData(JSON.parse(raw), handle);

      window.liveocareShippingTemplateCache[handle] = data;

      return data;
    } catch (error) {
      console.warn('LiveoCare Shipping: shipping-data Fehler:', error);
      return null;
    }
  };

  const loadShippingTemplateDataFromContext = async (root = document) => {
    const handle = getProductHandleFromContext(root);

    return loadShippingTemplateDataByHandle(handle);
  };

  const preloadShippingTemplateDataByHandles = async (handles = []) => {
    const uniqueHandles = handles
      .filter(Boolean)
      .filter((handle, index, allHandles) => allHandles.indexOf(handle) === index);

    if (!uniqueHandles.length) {
      return;
    }

    window.liveocareLastProductCardHandle = uniqueHandles[0];
    window.liveocareLastBuyAgainHandles = uniqueHandles;

    await Promise.all(uniqueHandles.map((handle) => loadShippingTemplateDataByHandle(handle)));
  };

  /***************** Shipping Date Helpers **********************/

  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  const toValidDays = (value, fallback) => {
    const parsed = parseInt(value, 10);

    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const addShippingDays = (amount) => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);

    let added = 0;

    while (added < amount) {
      date.setDate(date.getDate() + 1);

      if (date.getDay() !== 0) {
        added++;
      }
    }

    return date;
  };

  const formatDate = (date) => {
    const weekday = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];

    return `${weekday}. ${day} ${month}`;
  };

  const buildShippingText = (minDays, maxDays) => {
    const minDate = addShippingDays(minDays);
    const maxDate = addShippingDays(maxDays);

    return `ca. ${formatDate(minDate)} bis ${formatDate(maxDate)}`;
  };

  const buildShippingData = (minDays, maxDays) => {
    return {
      text: buildShippingText(minDays, maxDays),
      minDays: String(minDays),
      maxDays: String(maxDays)
    };
  };

  /***************** Form Helpers **********************/

  const ensureInput = (form, name) => {
    let input = form.querySelector(`input[name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    return input;
  };

  const getForm = (root = document) => {
    return (
      root.querySelector?.('form[data-type="add-to-cart-form"]') ||
      root.querySelector?.('form.main-atc-form') ||
      root.querySelector?.('form[action*="/cart/add"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('form.main-atc-form') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  };

  const writeShippingDataToForm = (form, shippingData) => {
    if (!form || !shippingData) {
      return '';
    }

    ensureInput(form, 'properties[Lieferung]').value = shippingData.text;
    ensureInput(form, 'properties[_shipping_min_days]').value = shippingData.minDays;
    ensureInput(form, 'properties[_shipping_max_days]').value = shippingData.maxDays;

    return shippingData.text;
  };

  /***************** Legacy DOM Source Fallback **********************/

  const getShippingSource = (root = document) => {
    return (
      root.querySelector?.('[data-shipping-text]') ||
      document.querySelector('[data-shipping-text]')
    );
  };

  const syncProperties = (root = document) => {
    const src = getShippingSource(root);
    const form = getForm(root);

    if (!src || !form) {
      return '';
    }

    const shippingData = {
      text: src.dataset.shippingText || '',
      minDays: src.dataset.shippingMinDays || '',
      maxDays: src.dataset.shippingMaxDays || ''
    };

    return writeShippingDataToForm(form, shippingData);
  };

  const syncPropertiesFromTemplate = async (root = document) => {
    const form = getForm(root);

    if (!form) {
      return '';
    }

    const templateData = await loadShippingTemplateDataFromContext(root);

    if (!templateData) {
      return syncProperties(root);
    }

    const minDays = toValidDays(templateData.minDays, 3);
    const maxDays = toValidDays(templateData.maxDays, 5);
    const shippingData = buildShippingData(minDays, maxDays);

    return writeShippingDataToForm(form, shippingData);
  };

  /***************** Cart Attribute **********************/

  const syncCartAttribute = async (text) => {
    if (!text) {
      return;
    }

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            Lieferung: text
          }
        })
      });
    } catch (error) {
      console.warn('Lieferzeit konnte nicht als Cart Attribute gespeichert werden:', error);
    }
  };

  /***************** Cart Shipping Sync **********************/

  const getCartLineHandle = (item) => {
    return (
      item.handle ||
      getProductHandleFromUrl(item.url || '') ||
      ''
    );
  };

  const getCleanCartProperties = (properties) => {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      return {};
    }

    return { ...properties };
  };

  const cartLineNeedsShippingUpdate = (properties, shippingData) => {
    return (
      String(properties.Lieferung || '') !== shippingData.text ||
      String(properties._shipping_min_days || '') !== shippingData.minDays ||
      String(properties._shipping_max_days || '') !== shippingData.maxDays
    );
  };

  const updateCartLineShippingProperties = async (line, item, shippingData) => {
    const properties = {
      ...getCleanCartProperties(item.properties),
      Lieferung: shippingData.text,
      _shipping_min_days: shippingData.minDays,
      _shipping_max_days: shippingData.maxDays
    };

    await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        line,
        quantity: item.quantity,
        properties
      })
    });
  };

  const syncCartShippingProperties = async () => {
    if (window.__liveocareCartShippingSyncRunning) {
      return;
    }

    window.__liveocareCartShippingSyncRunning = true;

    try {
      const cartResponse = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!cartResponse.ok) {
        return;
      }

      const cart = await cartResponse.json();

      if (!cart.items || !cart.items.length) {
        return;
      }

      let lastShippingText = '';

      for (let index = 0; index < cart.items.length; index++) {
        const item = cart.items[index];
        const line = index + 1;
        const handle = getCartLineHandle(item);

        if (!handle) {
          continue;
        }

        const templateData = await loadShippingTemplateDataByHandle(handle);

        if (!templateData) {
          continue;
        }

        const minDays = toValidDays(templateData.minDays, 3);
        const maxDays = toValidDays(templateData.maxDays, 5);
        const shippingData = buildShippingData(minDays, maxDays);
        const properties = getCleanCartProperties(item.properties);

        lastShippingText = shippingData.text;

        if (!cartLineNeedsShippingUpdate(properties, shippingData)) {
          continue;
        }

        await updateCartLineShippingProperties(line, item, shippingData);

        console.log('LiveoCare Cart Line Shipping synced:', {
          line,
          handle,
          text: shippingData.text,
          minDays: shippingData.minDays,
          maxDays: shippingData.maxDays
        });
      }

      await syncCartAttribute(lastShippingText);
    } catch (error) {
      console.warn('LiveoCare Shipping: Cart-Sync Fehler:', error);
    } finally {
      window.__liveocareCartShippingSyncRunning = false;
    }
  };

  const scheduleCartShippingSync = (delay = 400) => {
    if (window.__liveocareCartShippingSyncRunning) {
      return;
    }

    clearTimeout(window.__liveocareCartShippingSyncTimer);

    window.__liveocareCartShippingSyncTimer = setTimeout(() => {
      syncCartShippingProperties();
    }, delay);
  };

  /***************** CustomerHub Fetch Observer **********************/

  const getRequestUrl = (input) => {
    if (typeof input === 'string') {
      return input;
    }

    if (input instanceof URL) {
      return input.href;
    }

    if (input instanceof Request) {
      return input.url;
    }

    return input?.url || '';
  };

  const isCartJsonUrl = (url) => {
    if (!url) {
      return false;
    }

    try {
      const parsedUrl = new URL(url, window.location.origin);

      return parsedUrl.origin === window.location.origin && parsedUrl.pathname === '/cart.js';
    } catch (error) {
      return String(url) === '/cart.js';
    }
  };

  const captureGraphqlProductHandle = (url, init) => {
    if (!url || !String(url).includes('/graphql.json') || !init?.body) {
      return;
    }

    try {
      const body = JSON.parse(init.body);
      const handle = body?.variables?.handle || '';

      if (!handle) {
        return;
      }

      window.liveocareLastProductCardHandle = handle;

      loadShippingTemplateDataByHandle(handle);
    } catch (error) {
      return;
    }
  };

  const observeCustomerHubCartFetches = () => {
    if (window.__liveocareCustomerHubCartFetchObserved) {
      return;
    }

    window.__liveocareCustomerHubCartFetchObserved = true;

    const originalFetch = window.fetch;

    window.fetch = async function(input, init) {
      const url = getRequestUrl(input);

      captureGraphqlProductHandle(url, init);

      const response = await originalFetch.apply(this, arguments);

      if (isCartJsonUrl(url) && !window.__liveocareCartShippingSyncRunning) {
        scheduleCartShippingSync(500);
      }

      return response;
    };
  };

  observeCustomerHubCartFetches();

  /***************** Normal Product Add-To-Cart **********************/

  document.addEventListener('DOMContentLoaded', () => {
    syncProperties();
  });

  document.addEventListener('product-info:loaded', (event) => {
    syncProperties(event.target || document);
  });

  document.addEventListener('variant-change', () => {
    setTimeout(() => {
      syncProperties(document);
    }, 100);
  });

  document.addEventListener('submit', async (event) => {
    if (!event.target.matches('form[data-type="add-to-cart-form"], form.main-atc-form, form[action*="/cart/add"]')) {
      return;
    }

    if (event.target.dataset.liveocareShippingSubmitReady === 'true') {
      event.target.dataset.liveocareShippingSubmitReady = '';
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const form = event.target;
    const root = form.closest('.product__info-container, .product-action-modal__dialog, .product-action-modal, .modal, dialog') || document;
    const text = await syncPropertiesFromTemplate(root);

    await syncCartAttribute(text);

    form.dataset.liveocareShippingSubmitReady = 'true';

    if (form.requestSubmit) {
      form.requestSubmit();
      return;
    }

    form.submit();
  }, true);

  /***************** Product Card Trigger Shipping Prefetch **********************/

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.product-card-trigger');

    if (!button) {
      return;
    }

    const handle = getProductHandleFromProductCardTrigger(button);

    if (!handle) {
      return;
    }

    window.liveocareLastProductCardHandle = handle;

    loadShippingTemplateDataByHandle(handle);
  }, true);

  /***************** CustomerHub Buy Again Trigger **********************/

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.ord-inline-item .standard-ha .btn-primary, .ord-inline-item button.btn-primary');

    if (!button) {
      return;
    }

    const buttonText = button.textContent?.trim().toLowerCase() || '';

    if (!buttonText.includes('nochmal kaufen')) {
      return;
    }

    const orderRoot = button.closest('.ord-inline-item, .order');
    const handles = getProductHandlesFromOrderRoot(orderRoot);

    if (!handles.length) {
      return;
    }

    preloadShippingTemplateDataByHandles(handles);
    scheduleCartShippingSync(1200);
  }, true);

  /***************** CustomerHub Buy Again Modal Add-To-Cart **********************/

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.product-action-modal__dialog.ba-dlg .just-atc');

    if (!button) {
      return;
    }

    const modal = button.closest('.product-action-modal__dialog.ba-dlg');
    const handles = getProductHandlesFromLinks(modal);

    if (!handles.length) {
      scheduleCartShippingSync(1200);
      scheduleCartShippingSync(2200);
      return;
    }

    await preloadShippingTemplateDataByHandles(handles);

    scheduleCartShippingSync(1200);
    scheduleCartShippingSync(2200);
  }, true);

  /***************** Legacy CustomerHub Reorder Shipping Properties **********************/

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.chButton-Reorder[data-properties]');

    if (!button) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const parseProperties = (value) => {
      const result = {};

      value.split('&').forEach((part) => {
        if (!part) {
          return;
        }

        const index = part.indexOf('=');

        if (index === -1) {
          return;
        }

        const key = part.slice(0, index);

        let val = part.slice(index + 1).replace(/\+/g, ' ');

        try {
          val = decodeURIComponent(val);
        } catch (error) {
          console.warn('CustomerHub Property konnte nicht decodiert werden:', part);
        }

        result[key] = val;
      });

      return result;
    };

    const buildProperties = (properties) => {
      return Object.entries(properties)
        .map(([key, value]) => `${key}=${String(value).replace(/&/g, '%26')}`)
        .join('&') + '&';
    };

    const properties = parseProperties(button.dataset.properties || '');
    const reorderHandle = getProductHandleFromCustomerHubButton(button);
    const templateData = await loadShippingTemplateDataByHandle(reorderHandle);

    const fallbackMinDays = toValidDays(properties['properties[_shipping_min_days]'], 3);
    const fallbackMaxDays = toValidDays(properties['properties[_shipping_max_days]'], 5);

    const minDays = toValidDays(templateData?.minDays, fallbackMinDays);
    const maxDays = toValidDays(templateData?.maxDays, fallbackMaxDays);
    const shippingData = buildShippingData(minDays, maxDays);

    properties['properties[Lieferung]'] = shippingData.text;
    properties['properties[_shipping_min_days]'] = shippingData.minDays;
    properties['properties[_shipping_max_days]'] = shippingData.maxDays;

    button.dataset.properties = buildProperties(properties);

    await syncCartAttribute(shippingData.text);

    if (window.customerHub?.element?.reorderAddItems) {
      window.customerHub.element.reorderAddItems(button);
    }

    scheduleCartShippingSync(700);
  }, true);
})();

/***************** LiveoCare Shipping Properties On Cart Add END ***********************/
/***************************************************************************************/
